import { useEffect, useState } from 'react';
import { Box, Typography, Button, Stack, IconButton, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import VehicleServices from '../../services/externalServices/trackingservices/vehicleServices';
import { getOwnerDetailsFromLocalStorage, getOwnerIdFromLocalStorage } from '../../utils/auth';
import { useNavigate } from 'react-router-dom';
import{ Paper} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';

export default function VehiclesList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const navigate = useNavigate();

  /* ================= ROLE CHECK ================= */
  // const admin = JSON.parse(localStorage.getItem('admin') || '{}');
  // const isSuperAdmin = admin?.role === 'superAdmin';

  /* ================= FETCH VEHICLES ================= */
  const fetchAllVehicles = async () => {
    try {
      setLoading(true);
      setError(null);

      const ownerId = getOwnerIdFromLocalStorage();
      const ownerDetails=getOwnerDetailsFromLocalStorage();
      setIsSuperAdmin(ownerDetails?.role === 'superAdmin');
      if (!ownerId) {
        throw new Error('No owner access found. Please login again.');
      }

      const res = await VehicleServices.getVehiclesByOwnerId(ownerId);
      const vehicleData = res?.data?.vehicles || res?.data || res || [];
      setRows(vehicleData);
    } catch (e) {
      setError(e.message || 'Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllVehicles();
  }, []);

  const handleRowClick = (params) => {
    navigate(`/vehicles/${params.id}`);
  };

  /* ================= TABLE COLUMNS ================= */
  const columns = [
    { field: 'vehicle_number', headerName: 'Vehicle Number', flex: 1, minWidth: 160 },
    { field: 'alias_number', headerName: 'Alias', flex: 1, minWidth: 120 },
    { field: 'vehicle_type', headerName: 'Type', width: 120 },
    { field: 'trackingMethod', headerName: 'Tracking', width: 140 },
    { field: 'capacity', headerName: 'Capacity', type: 'number', width: 110 },
    { field: 'approval_status', headerName: 'Approval', width: 130 },
    // {
    //   field: 'created_at',
    //   headerName: 'Created',
    //   flex: 1,
    //   minWidth: 180,
    //   valueGetter: (params) => {
    //     const v = params.row?.created_at;
    //     return v ? new Date(v).toLocaleString() : '';
    //   },
    // },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 150,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          {/* VIEW – always visible */}
          <Tooltip title="View">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/vehicles/${params.row._id}`);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* EDIT – only super admin */}
          {isSuperAdmin && (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/vehicles/${params.row._id}/edit`);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Paper sx={{ p: 2 }}>
      {/* HEADER */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">Vehicles</Typography>

        <Stack direction="row" spacing={1}>
          {/* ADD VEHICLE – disabled for non super admin */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!isSuperAdmin}
            onClick={() => navigate('/vehicles/new')}
          >
            Add Vehicle
          </Button>

          <Button variant="outlined" onClick={fetchAllVehicles}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* ERROR */}
      {error && (
        <Typography color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {/* TABLE */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row._id}
          loading={loading}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          initialState={{
            pagination: { paginationModel: { pageSize: 12, page: 0 } },
          }}
          pageSizeOptions={[8, 12, 24, 50]}
        />
      </Box>
    </Paper>
  );
}
